import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import ConfirmDialog from '../components/ConfirmDialog';
import '../styles/pages/hotel-intake.css';

const API_URL = process.env.REACT_APP_API_BASE_URL || '';

const FOOD_TYPES = [
  { value: 'kibble',    label: 'Croqueta' },
  { value: 'wet',       label: 'Húmedo' },
  { value: 'raw',       label: 'BARF' },
  { value: 'homemade',  label: 'Casero' },
  { value: 'mixed',     label: 'Mixto' },
];

const FEAR_OPTIONS = [
  { value: 'thunder',    label: 'Truenos' },
  { value: 'fireworks',  label: 'Fuegos artificiales' },
  { value: 'strangers',  label: 'Personas desconocidas' },
  { value: 'dogs',       label: 'Otros perros' },
  { value: 'handling',   label: 'Manipulación' },
  { value: 'other',      label: 'Otro' },
];

const BEHAVIOR_OPTIONS = [
  { value: 'separation_anxiety', label: 'Ansiedad por separación' },
  { value: 'escape_artist',      label: 'Escapista' },
  { value: 'resource_guarding',  label: 'Protector con comida/juguetes' },
  { value: 'reactive',           label: 'Reactivo' },
  { value: 'barks_a_lot',        label: 'Ladra mucho' },
  { value: 'digs',               label: 'Hace hoyos' },
  { value: 'eats_objects',       label: 'Se come objetos' },
  { value: 'other',              label: 'Otro' },
];

const BELONGINGS = [
  { key: 'leash',       label: 'Correa' },
  { key: 'harness',     label: 'Pechera' },
  { key: 'collar',      label: 'Collar' },
  { key: 'waterBowl',   label: 'Plato de agua' },
  { key: 'foodBowl',    label: 'Plato de comida' },
  { key: 'bed',         label: 'Cama' },
  { key: 'blanket',     label: 'Cobija' },
  { key: 'toys',        label: 'Juguetes' },
  { key: 'food',        label: 'Alimento' },
  { key: 'medications', label: 'Medicamentos' },
];

// Maps a server validation error path (e.g. "belongings.leash.quantity") to
// something the person filling the form can act on. Without this the only
// signal was a generic "could not save" alert, which lost real work.
const FIELD_LABELS = {
  foodBrand: 'Marca del alimento',
  foodQuantity: 'Cantidad de alimento',
  treatsDescription: 'Premios permitidos',
  dietaryRestrictions: 'Restricciones alimenticias',
  energyLevel: 'Nivel de energía',
  fearsOther: 'Otro miedo',
  behaviorsOther: 'Otra conducta',
  specialInstructions: 'Indicaciones especiales',
  foodTypes: 'Tipo de alimento',
  fears: 'Miedos o detonantes',
  behaviors: 'Conductas importantes',
  medications: 'Medicamentos',
};

const FEEDING_LABELS = { feedingMorning: 'Mañana', feedingAfternoon: 'Tarde', feedingEvening: 'Noche' };
const SUBFIELD_LABELS = { time: 'horario', amount: 'cantidad', notes: 'indicaciones', quantity: 'cantidad', name: 'nombre', dose: 'dosis', schedule: 'horario', included: '¿trae?' };

function resolveFieldLabel(path = '') {
  const parts = path.split('.');

  if (parts[0] === 'belongings' && parts.length >= 2) {
    const item = BELONGINGS.find(b => b.key === parts[1]);
    const sub = SUBFIELD_LABELS[parts[2]];
    return `Pertenencias · ${item?.label || parts[1]}${sub ? ` — ${sub}` : ''}`;
  }

  if (FEEDING_LABELS[parts[0]]) {
    const sub = SUBFIELD_LABELS[parts[1]];
    return `Porciones · ${FEEDING_LABELS[parts[0]]}${sub ? ` — ${sub}` : ''}`;
  }

  if (parts[0] === 'medications' && parts.length >= 2) {
    const sub = SUBFIELD_LABELS[parts[2]];
    return `Medicamento ${Number(parts[1]) + 1}${sub ? ` — ${sub}` : ''}`;
  }

  return FIELD_LABELS[parts[0]] || path;
}

// The server caps belongings quantity at an integer 0–100. The input lets the
// field go briefly empty while retyping, so normalise only that transient state
// right before sending — a cleared box must not bounce the whole form. Values
// that are genuinely out of range are reported, not silently rewritten.
const normalizeBelongings = (belongings = {}) => Object.fromEntries(
  Object.entries(belongings).map(([key, item]) => {
    const blank = item?.quantity === '' || item?.quantity == null;
    return [key, blank ? { ...item, quantity: 1 } : item];
  })
);

// Character caps from the validators in routes/intake.js, keyed by the same
// paths the server reports in its 400 details. One source of truth for both the
// pre-flight check below and the message shown for a server rejection.
const MAX_MEDICATIONS = 50;
const TOP_LEVEL_LIMITS = {
  foodBrand: 200,
  foodQuantity: 200,
  treatsDescription: 500,
  dietaryRestrictions: 500,
  energyLevel: 200,
  fearsOther: 500,
  behaviorsOther: 500,
  specialInstructions: 2000,
};
const FEEDING_LIMITS = { time: 50, amount: 200, notes: 500 };
const MEDICATION_LIMITS = { name: 200, dose: 100, schedule: 200, notes: 500 };

// express-validator reports array indices in bracket form ("medications[0].name")
// but object keys dotted ("belongings.leash.notes"). Field keys on this page are
// dotted throughout, so normalise incoming server paths — otherwise a rejected
// medication field would never attach its message to the input it came from.
const normalizeErrorPath = (path = '') => String(path).replace(/\[(\d+)\]/g, '.$1');

function limitFor(path = '') {
  const parts = path.split('.');
  if (parts[0] === 'medications' && parts.length === 3) return MEDICATION_LIMITS[parts[2]];
  if (FEEDING_LABELS[parts[0]] && parts.length === 2) return FEEDING_LIMITS[parts[1]];
  if (parts[0] === 'belongings' && parts[2] === 'notes') return 500;
  return TOP_LEVEL_LIMITS[path];
}

// Mirrors the server rules so a rejection is caught before the request, with the
// offending field named. Runs against what is on screen, which matters for forms
// loaded with data saved before these caps existed: maxLength stops new typing
// but never shortens a value that arrived from the database.
function validateForm(form) {
  const errors = {};

  const checkLength = (path, value) => {
    const max = limitFor(path);
    const n = typeof value === 'string' ? value.trim().length : 0;
    if (max && n > max) {
      errors[path] = `Máximo ${max} caracteres (llevas ${n}). Recorta ${n - max}.`;
    }
  };

  Object.keys(TOP_LEVEL_LIMITS).forEach(key => checkLength(key, form[key]));

  Object.keys(FEEDING_LABELS).forEach(key => {
    Object.keys(FEEDING_LIMITS).forEach(sub => checkLength(`${key}.${sub}`, form[key]?.[sub]));
  });

  // The server caps these at 50 entries. The UI offers fixed option sets well
  // under that, so this only guards against data arriving from elsewhere.
  ['foodTypes', 'fears', 'behaviors'].forEach(key => {
    if (Array.isArray(form[key]) && form[key].length > 50) {
      errors[key] = `No se pueden guardar más de 50 opciones (tienes ${form[key].length}).`;
    }
  });

  if (Array.isArray(form.medications)) {
    if (form.medications.length > MAX_MEDICATIONS) {
      errors.medications = `No se pueden guardar más de ${MAX_MEDICATIONS} medicamentos (tienes ${form.medications.length}). Elimina algunos.`;
    }
    form.medications.forEach((med, i) => {
      Object.keys(MEDICATION_LIMITS).forEach(sub => checkLength(`medications.${i}.${sub}`, med?.[sub]));
    });
  }

  BELONGINGS.forEach(({ key }) => {
    const item = form.belongings?.[key];
    if (!item) return;
    checkLength(`belongings.${key}.notes`, item.notes);
    // An empty box is normalised to 1 on submit, so it is not an error. The
    // range matches the server's isInt({ min: 0, max: 100 }) exactly — being
    // stricter here would block a save the server would have accepted.
    if (item.included && item.quantity !== '' && item.quantity != null) {
      const n = Number(item.quantity);
      if (!Number.isInteger(n) || n < 0 || n > 100) {
        errors[`belongings.${key}.quantity`] = 'Debe ser un número entero entre 0 y 100.';
      }
    }
  });

  return errors;
}

// Medication errors are keyed by row index, so they have to be rebuilt whenever
// rows shift rather than carried across.
const MED_ERROR_KEY = /^medications(\.|$)/;
const stripMedicationErrors = (errors) =>
  Object.fromEntries(Object.entries(errors).filter(([k]) => !MED_ERROR_KEY.test(k)));
const pickMedicationErrors = (errors) =>
  Object.fromEntries(Object.entries(errors).filter(([k]) => MED_ERROR_KEY.test(k)));

const emptyForm = () => ({
  submittedBy: 'owner',
  foodBrand: '',
  foodTypes: [],
  foodQuantity: '',
  feedingMorning:   { time: '', amount: '', notes: '' },
  feedingAfternoon: { time: '', amount: '', notes: '' },
  feedingEvening:   { time: '', amount: '', notes: '' },
  treatsAllowed: null,
  treatsDescription: '',
  dietaryRestrictions: '',
  noMedications: false,
  medications: [],
  socialWithDogs: null,
  energyLevel: '',
  fears: [],
  fearsOther: '',
  canStayAlone: null,
  behaviors: [],
  behaviorsOther: '',
  specialInstructions: '',
  belongings: Object.fromEntries(BELONGINGS.map(b => [b.key, { included: false, quantity: 1, notes: '' }])),
});

function formatDate(d) {
  if (!d) return '';
  const s = typeof d === 'string' ? d : new Date(d).toISOString();
  const [y, m, day] = s.slice(0, 10).split('-');
  return `${day}/${m}/${y}`;
}

// A saved intake form as the page edits it, or a blank one if never submitted.
const formFrom = (intakeForm) => {
  if (!intakeForm?.submittedAt) return emptyForm();
  return {
    ...emptyForm(),
    ...intakeForm,
    belongings: { ...emptyForm().belongings, ...intakeForm.belongings },
  };
};

// One dog's questions. Errors are this dog's own, keyed by the server's paths.
function DogIntakeFields({ form, fieldErrors, set, toggleArr, addMedication, removeMedication, setMedication }) {
  // Error presentation helpers. `cls` merges the invalid style onto a field's
  // existing class, `fieldErr` renders the message under a single field, and
  // `rowErrors` collects messages for a grid row that cannot host a child div.
  const cls = (path, base) =>
    [base, fieldErrors[path] ? 'intake-invalid' : ''].filter(Boolean).join(' ') || undefined;

  const fieldErr = (path) =>
    fieldErrors[path] ? <div className="intake-field-error">{fieldErrors[path]}</div> : null;

  const rowErrors = (paths) => {
    const hit = paths.filter(p => fieldErrors[p]);
    if (!hit.length) return null;
    return (
      <div className="intake-row-error">
        {hit.map(p => {
          const sub = SUBFIELD_LABELS[p.split('.').pop()];
          return <div key={p}>{sub ? `${sub[0].toUpperCase()}${sub.slice(1)}: ` : ''}{fieldErrors[p]}</div>;
        })}
      </div>
    );
  };

  return (
    <>
      {/* ── Alimentación ─────────────────────────────── */}
      <section className="intake-section">
        <h2 className="intake-section-title">🍽️ Alimentación</h2>

        <div className="intake-field">
          <label>Marca del alimento</label>
          <input className={cls('foodBrand')} aria-invalid={!!fieldErrors['foodBrand']} maxLength={200} value={form.foodBrand} onChange={e => set('foodBrand', e.target.value)} placeholder="Ej. Royal Canin" />
          {fieldErr('foodBrand')}
        </div>

        <div className="intake-field">
          <label>Tipo de alimento</label>
          <div className="intake-checkbox-row">
            {FOOD_TYPES.map(f => (
              <label key={f.value} className="intake-check-label">
                <input type="checkbox" checked={form.foodTypes.includes(f.value)} onChange={() => toggleArr('foodTypes', f.value)} />
                {f.label}
              </label>
            ))}
          </div>
          {fieldErr('foodTypes')}
          <input
            style={{ marginTop: 8 }}
            className={cls('foodQuantity')}
            aria-invalid={!!fieldErrors['foodQuantity']}
            maxLength={200}
            value={form.foodQuantity}
            onChange={e => set('foodQuantity', e.target.value)}
            placeholder="Ej. 2 bolsas de croqueta, 1 lata de húmedo"
          />
          {fieldErr('foodQuantity')}
        </div>

        <div className="intake-field">
          <label>Porciones y horarios</label>
          <div className="intake-feeding-table">
            <div className="intake-feeding-header">
              <span>Momento</span><span>Horario</span><span>Cantidad</span><span>Indicaciones</span>
            </div>
            {[
              { key: 'feedingMorning',   label: 'Mañana' },
              { key: 'feedingAfternoon', label: 'Tarde' },
              { key: 'feedingEvening',   label: 'Noche' },
            ].map(({ key, label }) => (
              <React.Fragment key={key}>
                <div className="intake-feeding-row">
                  <span className="intake-feeding-period">{label}</span>
                  <input type="time" className={cls(`${key}.time`)} aria-invalid={!!fieldErrors[`${key}.time`]} value={form[key].time} onChange={e => set(`${key}.time`, e.target.value)} />
                  <input maxLength={200} className={cls(`${key}.amount`)} aria-invalid={!!fieldErrors[`${key}.amount`]} placeholder="Ej. 1 taza" value={form[key].amount} onChange={e => set(`${key}.amount`, e.target.value)} />
                  <input maxLength={500} className={cls(`${key}.notes`)} aria-invalid={!!fieldErrors[`${key}.notes`]} placeholder="Indicaciones" value={form[key].notes} onChange={e => set(`${key}.notes`, e.target.value)} />
                </div>
                {rowErrors([`${key}.time`, `${key}.amount`, `${key}.notes`])}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="intake-field">
          <label>¿Puede comer premios?</label>
          <div className="intake-radio-row">
            <label className="intake-radio-label">
              <input type="radio" checked={form.treatsAllowed === true} onChange={() => set('treatsAllowed', true)} /> Sí
            </label>
            <label className="intake-radio-label">
              <input type="radio" checked={form.treatsAllowed === false} onChange={() => set('treatsAllowed', false)} /> No
            </label>
          </div>
        </div>

        {form.treatsAllowed && (
          <div className="intake-field">
            <label>¿Qué premios puede comer?</label>
            <input className={cls('treatsDescription')} aria-invalid={!!fieldErrors['treatsDescription']} maxLength={500} value={form.treatsDescription} onChange={e => set('treatsDescription', e.target.value)} placeholder="Ej. Galletas de arroz" />
            {fieldErr('treatsDescription')}
          </div>
        )}

        <div className="intake-field">
          <label>Restricciones alimenticias / alergias</label>
          <input className={cls('dietaryRestrictions')} aria-invalid={!!fieldErrors['dietaryRestrictions']} maxLength={500} value={form.dietaryRestrictions} onChange={e => set('dietaryRestrictions', e.target.value)} placeholder="Ej. Sin pollo, alérgico al trigo" />
          {fieldErr('dietaryRestrictions')}
        </div>
      </section>

      {/* ── Medicamentos ─────────────────────────────── */}
      <section className="intake-section">
        <h2 className="intake-section-title">💊 Medicamentos</h2>

        <div className="intake-field">
          <label className="intake-check-label intake-check-label--big">
            <input type="checkbox" checked={form.noMedications} onChange={e => set('noMedications', e.target.checked)} />
            No toma medicamentos
          </label>
        </div>

        {!form.noMedications && (
          <>
            {form.medications.map((med, i) => (
              <React.Fragment key={i}>
                <div className="intake-med-row">
                  <input maxLength={200} className={cls(`medications.${i}.name`)} aria-invalid={!!fieldErrors[`medications.${i}.name`]} placeholder="Medicamento" value={med.name} onChange={e => setMedication(i, 'name', e.target.value)} />
                  <input maxLength={100} className={cls(`medications.${i}.dose`)} aria-invalid={!!fieldErrors[`medications.${i}.dose`]} placeholder="Dosis" value={med.dose} onChange={e => setMedication(i, 'dose', e.target.value)} />
                  <input maxLength={200} className={cls(`medications.${i}.schedule`)} aria-invalid={!!fieldErrors[`medications.${i}.schedule`]} placeholder="Horario" value={med.schedule} onChange={e => setMedication(i, 'schedule', e.target.value)} />
                  <input maxLength={500} className={cls(`medications.${i}.notes`)} aria-invalid={!!fieldErrors[`medications.${i}.notes`]} placeholder="Indicaciones" value={med.notes} onChange={e => setMedication(i, 'notes', e.target.value)} />
                  <button type="button" className="intake-remove-btn" onClick={() => removeMedication(i)}>✕</button>
                </div>
                {rowErrors([`medications.${i}.name`, `medications.${i}.dose`, `medications.${i}.schedule`, `medications.${i}.notes`])}
              </React.Fragment>
            ))}
            {fieldErr('medications')}
            <button
              type="button"
              className="intake-add-btn"
              onClick={addMedication}
              disabled={form.medications.length >= MAX_MEDICATIONS}
            >
              + Agregar medicamento
            </button>
          </>
        )}
      </section>

      {/* ── Comportamiento ───────────────────────────── */}
      <section className="intake-section">
        <h2 className="intake-section-title">🐾 Comportamiento y Cuidados</h2>

        <div className="intake-field">
          <label>¿Convive con otros perros?</label>
          <div className="intake-radio-row">
            <label className="intake-radio-label"><input type="radio" checked={form.socialWithDogs === true} onChange={() => set('socialWithDogs', true)} /> Sí</label>
            <label className="intake-radio-label"><input type="radio" checked={form.socialWithDogs === false} onChange={() => set('socialWithDogs', false)} /> No</label>
          </div>
        </div>

        <div className="intake-field">
          <label>Nivel de energía</label>
          <div className="intake-radio-row">
            {[{ v: 'low', l: 'Bajo' }, { v: 'medium', l: 'Medio' }, { v: 'high', l: 'Alto' }].map(({ v, l }) => (
              <label key={v} className="intake-radio-label">
                <input type="radio" checked={form.energyLevel === v} onChange={() => set('energyLevel', v)} /> {l}
              </label>
            ))}
          </div>
        </div>

        <div className="intake-field">
          <label>Miedos o detonantes</label>
          <div className="intake-checkbox-col">
            {FEAR_OPTIONS.map(f => (
              <label key={f.value} className="intake-check-label">
                <input type="checkbox" checked={form.fears.includes(f.value)} onChange={() => toggleArr('fears', f.value)} />
                {f.label}
              </label>
            ))}
          </div>
          {fieldErr('fears')}
          {form.fears.includes('other') && (
            <input className={cls('fearsOther', 'intake-other-input')} aria-invalid={!!fieldErrors['fearsOther']} maxLength={500} placeholder="Especificar otro miedo" value={form.fearsOther} onChange={e => set('fearsOther', e.target.value)} />
          )}
          {fieldErr('fearsOther')}
        </div>

        <div className="intake-field">
          <label>¿Puede quedarse solo?</label>
          <div className="intake-radio-row">
            <label className="intake-radio-label"><input type="radio" checked={form.canStayAlone === true} onChange={() => set('canStayAlone', true)} /> Sí</label>
            <label className="intake-radio-label"><input type="radio" checked={form.canStayAlone === false} onChange={() => set('canStayAlone', false)} /> No</label>
          </div>
        </div>

        <div className="intake-field">
          <label>Conductas importantes</label>
          <div className="intake-checkbox-col">
            {BEHAVIOR_OPTIONS.map(b => (
              <label key={b.value} className="intake-check-label">
                <input type="checkbox" checked={form.behaviors.includes(b.value)} onChange={() => toggleArr('behaviors', b.value)} />
                {b.label}
              </label>
            ))}
          </div>
          {fieldErr('behaviors')}
          {form.behaviors.includes('other') && (
            <input className={cls('behaviorsOther', 'intake-other-input')} aria-invalid={!!fieldErrors['behaviorsOther']} maxLength={500} placeholder="Especificar otra conducta" value={form.behaviorsOther} onChange={e => set('behaviorsOther', e.target.value)} />
          )}
          {fieldErr('behaviorsOther')}
        </div>

        <div className="intake-field">
          <label>Indicaciones especiales</label>
          <textarea rows={3} className={cls('specialInstructions')} aria-invalid={!!fieldErrors['specialInstructions']} maxLength={2000} value={form.specialInstructions} onChange={e => set('specialInstructions', e.target.value)} placeholder="Cualquier información importante para el cuidado de tu perro" />
          {fieldErr('specialInstructions')}
        </div>
      </section>

      {/* ── Pertenencias ─────────────────────────────── */}
      <section className="intake-section">
        <h2 className="intake-section-title">🎒 Pertenencias Entregadas</h2>
        <div className="intake-belongings-table">
          <div className="intake-belongings-header">
            <span>Artículo</span><span>¿Trae?</span><span>Cantidad</span><span>Observaciones</span>
          </div>
          {BELONGINGS.map(({ key, label }) => (
            <React.Fragment key={key}>
              <div className="intake-belongings-row">
                <span className="intake-belonging-name">{label}</span>
                <label className="intake-check-toggle">
                  <input
                    type="checkbox"
                    checked={form.belongings[key]?.included || false}
                    onChange={e => set(`belongings.${key}.included`, e.target.checked)}
                  />
                </label>
                {form.belongings[key]?.included ? (
                  <>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      className={cls(`belongings.${key}.quantity`, 'intake-qty-input')}
                      aria-invalid={!!fieldErrors[`belongings.${key}.quantity`]}
                      value={form.belongings[key]?.quantity ?? 1}
                      onChange={e => {
                        const raw = e.target.value.replace(/[^0-9]/g, '');
                        set(`belongings.${key}.quantity`, raw === '' ? '' : parseInt(raw, 10));
                      }}
                      onBlur={() => {
                        const cur = form.belongings[key]?.quantity;
                        if (cur === '' || cur == null) set(`belongings.${key}.quantity`, 1);
                      }}
                    />
                    <input
                      placeholder="Características"
                      maxLength={500}
                      className={cls(`belongings.${key}.notes`)}
                      aria-invalid={!!fieldErrors[`belongings.${key}.notes`]}
                      value={form.belongings[key]?.notes || ''}
                      onChange={e => set(`belongings.${key}.notes`, e.target.value)}
                    />
                  </>
                ) : (
                  <><span /><span /></>
                )}
              </div>
              {rowErrors([`belongings.${key}.quantity`, `belongings.${key}.notes`])}
            </React.Fragment>
          ))}
        </div>
      </section>
    </>
  );
}

export default function HotelIntakeFormPage({ staffMode = false }) {
  const { bookingId } = useParams();
  const navigate = useNavigate();

  const [booking, setBooking] = useState(null);
  // Every dog of the reservation, each saved to its own booking's token. A
  // single-dog booking is just a list of one.
  const [dogs, setDogs] = useState([]);
  const [forms, setForms] = useState([]);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  // Why the last save attempt failed: { message, fields? }. Shown in place, with
  // the form still filled in, instead of a generic alert that lost the work.
  const [saveError, setSaveError] = useState(null);
  // Per-field messages, one object per dog, keyed by the server's own field
  // paths. Populated either by the pre-flight check or by a server 400, so
  // both render identically.
  const [fieldErrors, setFieldErrors] = useState([]);
  const errorRef = useRef(null);

  // Tracks the forms' state as of the last load or save, to detect unsaved changes
  const initialSnapshotRef = useRef(null);
  const isDirtyRef = useRef(false); // read by the beforeunload listener
  const [isDirty, setIsDirty] = useState(false); // same flag, for rendering
  // Whether a saved version exists to fall back to. Without one, discarding
  // empties the form outright, which the confirmation has to say plainly.
  const [hasSavedVersion, setHasSavedVersion] = useState(false);
  // Navigation held until the user confirms leaving unsaved work.
  const [pendingExit, setPendingExit] = useState(null);
  // A Cancelar press held until the user confirms discarding what they typed.
  const [pendingDiscard, setPendingDiscard] = useState(false);
  // What a discard threw away, so a misclick can be taken back.
  const [discarded, setDiscarded] = useState(null);

  useEffect(() => {
    if (!bookingId) return;
    fetch(`${API_URL}/api/hotel-intake/${bookingId}`)
      .then(r => r.ok ? r.json() : Promise.reject('No encontrado'))
      .then(data => {
        setBooking(data);
        const list = data.dogs?.length
          ? data.dogs
          : [{ token: bookingId, dogName: data.dogName, intakeForm: data.intakeForm }];
        const f = list.map(d => formFrom(d.intakeForm));
        setDogs(list);
        setForms(f);
        setFieldErrors(list.map(() => ({})));
        setHasSavedVersion(list.some(d => d.intakeForm?.submittedAt));
        initialSnapshotRef.current = JSON.stringify(f);
      })
      .catch(() => setLoadError('No pudimos cargar la información de la reserva.'));
  }, [bookingId]);

  // Bring a failed save into view once the message has actually rendered
  useEffect(() => {
    // Optional call: scrolling is a nicety, and a throw here would unmount the
    // form mid-error — the one moment the typed data must stay on screen.
    if (saveError) errorRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  }, [saveError]);

  // Report unsaved-changes state so navigation attempts can be guarded
  useEffect(() => {
    if (!initialSnapshotRef.current) return;
    const dirty = JSON.stringify(forms) !== initialSnapshotRef.current;
    isDirtyRef.current = dirty;
    setIsDirty(dirty);
  }, [forms]);

  // Warn on browser tab close/refresh if there are unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirtyRef.current) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // Leaving the page. Runs `action` immediately unless there is unsaved work, in
  // which case it waits for confirmation. It must NOT touch the form: resetting
  // state on the way out is what made a misclick unrecoverable.
  const guardExit = (action) => {
    if (isDirtyRef.current) {
      setPendingExit(() => action);
    } else {
      action();
    }
  };

  const confirmExit = () => {
    const action = pendingExit;
    setPendingExit(null);
    if (action) action();
  };

  // Cancelar: discard what was typed and go back to the last saved version. Only
  // reachable while there is something to discard, and always undoable — this
  // button sits next to Guardar, so a misclick must not cost the form.
  const requestDiscard = () => {
    if (isDirtyRef.current) setPendingDiscard(true);
  };

  const confirmDiscard = () => {
    setPendingDiscard(false);
    setDiscarded(JSON.stringify(forms));
    if (initialSnapshotRef.current) setForms(JSON.parse(initialSnapshotRef.current));
    setFieldErrors(dogs.map(() => ({})));
    setSaveError(null);
  };

  const undoDiscard = () => {
    if (!discarded) return;
    setForms(JSON.parse(discarded));
    setDiscarded(null);
  };

  const closeDialog = () => {
    setPendingExit(null);
    setPendingDiscard(false);
  };

  // Any new edit supersedes a discard, so the undo offer retires rather than
  // risking overwriting content typed after it.
  const noteEdit = () => setDiscarded(prev => (prev === null ? prev : null));

  const updateForm = (idx, fn) => setForms(prev => prev.map((f, i) => (i === idx ? fn(f) : f)));

  // Drop a field's error as soon as it is edited, so a fix visibly resolves.
  const clearFieldError = (idx, ...paths) => {
    setFieldErrors(prev => {
      const mine = prev[idx] || {};
      if (!paths.some(p => mine[p])) return prev;
      const next = { ...mine };
      paths.forEach(p => delete next[p]);
      return prev.map((e, i) => (i === idx ? next : e));
    });
  };

  const setDogErrors = (idx, errors) =>
    setFieldErrors(prev => prev.map((e, i) => (i === idx ? errors : e)));

  // The edit handlers for one dog's section.
  const handlersFor = (idx) => ({
    set: (path, value) => {
      clearFieldError(idx, path);
      noteEdit();
      updateForm(idx, prev => {
        const next = { ...prev };
        const keys = path.split('.');
        let cur = next;
        for (let i = 0; i < keys.length - 1; i++) {
          cur[keys[i]] = { ...cur[keys[i]] };
          cur = cur[keys[i]];
        }
        cur[keys[keys.length - 1]] = value;
        return next;
      });
    },
    toggleArr: (field, value) => {
      clearFieldError(idx, field);
      noteEdit();
      updateForm(idx, prev => ({
        ...prev,
        [field]: prev[field].includes(value)
          ? prev[field].filter(v => v !== value)
          : [...prev[field], value],
      }));
    },
    addMedication: () => {
      clearFieldError(idx, 'medications');
      noteEdit();
      updateForm(idx, p => ({ ...p, medications: [...p.medications, { name: '', dose: '', schedule: '', notes: '' }] }));
    },
    removeMedication: i => {
      noteEdit();
      // Row indices shift, so index-keyed errors below the removed row would point
      // at the wrong medication — re-derive them from the resulting form instead.
      const next = { ...forms[idx], medications: forms[idx].medications.filter((_, n) => n !== i) };
      updateForm(idx, () => next);
      setDogErrors(idx, { ...stripMedicationErrors(fieldErrors[idx] || {}), ...pickMedicationErrors(validateForm(next)) });
    },
    setMedication: (i, field, value) => {
      clearFieldError(idx, `medications.${i}.${field}`);
      noteEdit();
      updateForm(idx, p => ({
        ...p,
        medications: p.medications.map((m, n) => n === i ? { ...m, [field]: value } : m),
      }));
    },
  });

  // Siblings usually eat the same food and come with the same instructions, so
  // a dog's section can start from the previous one's answers.
  const copyFromPrevious = (idx) => {
    if (idx < 1) return;
    noteEdit();
    const source = JSON.parse(JSON.stringify(forms[idx - 1]));
    updateForm(idx, () => source);
    setDogErrors(idx, {});
  };

  const multi = dogs.length > 1;
  // Field names in a summary carry the dog's name once there is more than one.
  const labelFor = (idx, path) => (multi ? `${dogs[idx].dogName} · ` : '') + resolveFieldLabel(path);

  // Save one dog. Resolves to null on success, or { errors?, message } on failure.
  const saveDog = async (idx) => {
    const payload = {
      ...forms[idx],
      submittedBy: staffMode ? 'staff' : 'owner',
      belongings: normalizeBelongings(forms[idx].belongings),
    };
    const res = await fetch(`${API_URL}/api/hotel-intake/${dogs[idx].token}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (res.ok) return null;

    const data = await res.json().catch(() => null);
    if (res.status === 400 && Array.isArray(data?.details) && data.details.length) {
      // Land each rejection on its own field. The server's own message is
      // "Invalid value", so say what the cap actually is where we know it.
      const errors = {};
      data.details.forEach(d => {
        const field = normalizeErrorPath(d.field);
        const max = limitFor(field);
        errors[field] = max
          ? `Máximo ${max} caracteres.`
          : 'Este valor no es válido. Corrígelo y vuelve a guardar.';
      });
      return { status: 400, errors };
    }
    return { status: res.status };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Catch anything the server would reject before sending, so the problem is
    // named on the field itself and the form stays filled in and editable.
    const preflight = forms.map(validateForm);
    if (preflight.some(p => Object.keys(p).length)) {
      setFieldErrors(preflight);
      setSaveError({
        message: 'Revisa los campos marcados en rojo. Tus datos siguen aquí — corrígelos y vuelve a presionar Guardar.',
        fields: preflight.flatMap((p, idx) => Object.keys(p).map(path => labelFor(idx, path))),
      });
      return;
    }

    setSaving(true);
    setSaveError(null);
    setFieldErrors(dogs.map(() => ({})));
    try {
      // Each dog is its own booking. Saving them all again after a partial
      // failure is harmless, so a retry simply resends everything.
      const results = await Promise.all(forms.map((_, idx) => saveDog(idx).catch(() => ({ status: 0 }))));
      const failures = results.map((r, idx) => (r ? { ...r, idx } : null)).filter(Boolean);

      // Whatever did save is now the version a discard returns to.
      const snapshot = JSON.parse(initialSnapshotRef.current);
      results.forEach((r, idx) => { if (!r) snapshot[idx] = forms[idx]; });
      initialSnapshotRef.current = JSON.stringify(snapshot);
      if (failures.length < results.length) setHasSavedVersion(true);

      if (failures.length) {
        // Keep the form exactly as typed and say what actually went wrong, so
        // the work can be saved instead of silently lost.
        const dogsFailed = multi ? ` (${failures.map(f => dogs[f.idx].dogName).join(', ')})` : '';
        const serverRejected = failures.filter(f => f.errors);
        setFieldErrors(dogs.map((_, idx) => failures.find(f => f.idx === idx)?.errors || {}));
        isDirtyRef.current = JSON.stringify(forms) !== initialSnapshotRef.current;
        setIsDirty(isDirtyRef.current);

        if (failures.some(f => f.status === 429)) {
          setSaveError({
            message: `El servidor recibió demasiadas solicitudes${dogsFailed}. Espera unos minutos y vuelve a presionar Guardar — no cierres esta página, tus datos siguen aquí.`,
          });
        } else if (serverRejected.length) {
          setSaveError({
            message: `El servidor rechazó estos campos${dogsFailed}. Tus datos siguen aquí — corrígelos y vuelve a presionar Guardar.`,
            fields: [...new Set(serverRejected.flatMap(f => Object.keys(f.errors).map(path => labelFor(f.idx, path))))],
          });
        } else if (failures.some(f => f.status === 404)) {
          setSaveError({ message: 'No encontramos esta reserva. Verifica el enlace o pide uno nuevo.' });
        } else if (failures.every(f => f.status === 0)) {
          setSaveError({
            message: 'No pudimos conectarnos con el servidor. Revisa tu conexión — tus datos siguen aquí e intenta de nuevo.',
          });
        } else {
          setSaveError({
            message: `No pudimos guardar el formulario${dogsFailed} (error ${failures.map(f => f.status).filter(Boolean).join(', ')}). Tus datos siguen aquí — intenta de nuevo.`,
          });
        }
        return;
      }

      isDirtyRef.current = false;
      setIsDirty(false);
      setDiscarded(null);
      setSaved(true);
      window.scrollTo(0, 0);
    } finally {
      setSaving(false);
    }
  };

  if (loadError) {
    return (
      <div className="intake-wrap">
        <div className="intake-error">{loadError}</div>
      </div>
    );
  }

  if (!booking) {
    return <div className="intake-wrap"><div className="intake-loading">Cargando…</div></div>;
  }

  const dogNames = dogs.map(d => d.dogName).join(', ');

  if (saved) {
    return (
      <div className="intake-wrap">
        <div className="intake-success">
          <div className="intake-success-icon">✅</div>
          <h2>¡Formulario enviado!</h2>
          <p>Gracias por completar el formulario de ingreso para <strong>{dogNames}</strong>.</p>
          <p className="intake-success-dates">
            🏨 Hospedaje: {formatDate(booking.checkIn)} → {formatDate(booking.checkOut)}
          </p>
          {staffMode && (
            <Link to="/hotel" className="intake-back-btn">
              ← Volver a hotel
            </Link>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="intake-wrap">
      {/* Header */}
      <div className="intake-header">
        {staffMode && (
          <button type="button" className="intake-back-btn-sm" onClick={() => guardExit(() => navigate('/hotel'))}>‹ Hotel</button>
        )}
        <div className="intake-header-logo">🐾 Happy Tails Hotel</div>
        <h1 className="intake-title">Formulario de Ingreso</h1>
        <div className="intake-booking-info">
          <span className="intake-dog-name">{dogNames}</span>
          <span className="intake-dates">
            {formatDate(booking.checkIn)} → {formatDate(booking.checkOut)}
          </span>
        </div>
      </div>

      <form className="intake-form" onSubmit={handleSubmit}>

        {dogs.map((dog, idx) => (
          <React.Fragment key={dog.token}>
            {multi && (
              <div className="intake-dog-banner">
                <span className="intake-dog-banner-name">🐕 {dog.dogName}</span>
                {idx > 0 && (
                  <button type="button" className="intake-copy-btn" onClick={() => copyFromPrevious(idx)}>
                    Copiar respuestas de {dogs[idx - 1].dogName}
                  </button>
                )}
              </div>
            )}
            <DogIntakeFields
              form={forms[idx]}
              fieldErrors={fieldErrors[idx] || {}}
              {...handlersFor(idx)}
            />
          </React.Fragment>
        ))}

        {saveError && (
          <div className="intake-save-error" ref={errorRef} role="alert">
            <div className="intake-save-error-title">⚠️ No se guardó el formulario</div>
            <p>{saveError.message}</p>
            {saveError.fields?.length > 0 && (
              <ul className="intake-save-error-fields">
                {saveError.fields.map(f => <li key={f}>{f}</li>)}
              </ul>
            )}
          </div>
        )}

        {discarded && (
          <div className="intake-undo-notice" role="status">
            <span>Se descartó lo que habías capturado.</span>
            <button type="button" className="intake-undo-btn" onClick={undoDiscard}>
              Recuperar
            </button>
          </div>
        )}

        <div className="intake-submit-wrap">
          <button
            type="button"
            className="intake-cancel-btn"
            disabled={saving || !isDirty}
            title={isDirty ? 'Descartar lo capturado y volver a la última versión guardada' : 'No hay cambios por descartar'}
            onClick={requestDiscard}
          >
            Descartar cambios
          </button>
          <button type="submit" className="intake-submit-btn" disabled={saving}>
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </form>

      <ConfirmDialog
        isOpen={pendingExit !== null}
        icon="🐾"
        title="¡Espera! Tienes cambios sin guardar"
        message="Si sales ahora, se perderán los cambios que hiciste en este formulario."
        confirmLabel="Salir sin guardar"
        cancelLabel="Seguir editando"
        danger
        onConfirm={confirmExit}
        onCancel={closeDialog}
      />

      <ConfirmDialog
        isOpen={pendingDiscard}
        icon="🗑️"
        title="¿Descartar lo que capturaste?"
        message={hasSavedVersion
          ? 'El formulario volverá a la última versión guardada. Lo que escribiste después de esa versión se borrará, aunque podrás recuperarlo con el botón "Recuperar".'
          : 'Este formulario nunca se ha guardado, así que todos los campos quedarán vacíos. Podrás recuperarlo con el botón "Recuperar" si fue un error.'}
        confirmLabel="Sí, descartar"
        cancelLabel="Seguir editando"
        danger
        onConfirm={confirmDiscard}
        onCancel={closeDialog}
      />
    </div>
  );
}
